<script setup lang="ts">
/**
 * 字体字段：点开一份**平铺的字体清单**，每行用它自己的字体渲染。
 *
 * 两个刻意的取舍：
 *  · 清单里是**具体字体名**，不是「中文无衬线 / 等宽」这类类别名 —— 类别名写进文件后，
 *    作者看不出自己实际用的是哪款字体，换台机器也无从回落。
 *  · 选中**不自动收起** —— 连着试几个字体看效果是常态（与色板浮层同一条规矩）。
 */
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { FONT_LIST } from '@/core/query/text';

const props = defineProps<{ value: string }>();
const emit = defineEmits<{ change: [value: string] }>();

const open = ref(false);
const anchor = ref<HTMLElement | null>(null);
const style = ref<Record<string, string>>({});

const matched = computed(() => FONT_LIST.find((f) => f.value === props.value));
/** 文档里的字体不在清单里（作者手写的栈）→ 显示栈里第一款字体名，别假装是清单里的某一项 */
const label = computed(
  () =>
    matched.value?.label ??
    (props.value.split(',')[0] ?? '').trim().replace(/^['"]|['"]$/g, ''),
);

const PANEL_W = 236;

const position = (): void => {
  const el = anchor.value;
  if (!el) return;
  const r = el.getBoundingClientRect();
  const below = window.innerHeight - r.bottom - 12;
  const up = below < 200 && r.top > below; // 下面放不下就翻上去
  const maxHeight = Math.max(140, Math.min(340, up ? r.top - 16 : below));
  style.value = {
    left: `${Math.round(Math.max(8, Math.min(r.left, window.innerWidth - PANEL_W - 8)))}px`,
    top: `${Math.round(up ? r.top - maxHeight - 6 : r.bottom + 6)}px`,
    maxHeight: `${Math.round(maxHeight)}px`,
  };
};

const toggle = (): void => {
  open.value = !open.value;
};

const onDocDown = (e: Event): void => {
  const t = e.target;
  if (t instanceof Element && (t.closest('.font-pop') || t.closest('.font-trigger'))) return;
  open.value = false;
};
const onKey = (e: KeyboardEvent): void => {
  if (e.key !== 'Escape') return;
  // 捕获阶段先吃掉 Esc：否则同一次 Esc 会被全局热键拿去清空选中
  e.stopPropagation();
  open.value = false;
};

watch(open, (isOpen) => {
  if (isOpen) {
    position();
    document.addEventListener('pointerdown', onDocDown, true);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('resize', position);
  } else {
    document.removeEventListener('pointerdown', onDocDown, true);
    document.removeEventListener('keydown', onKey, true);
    window.removeEventListener('resize', position);
  }
});

/** 切选中元素 / 文档变更后锚点位置会变；浮层开着就跟着挪，免得指错地方 */
watch(() => props.value, () => {
  if (open.value) position();
});

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onDocDown, true);
  document.removeEventListener('keydown', onKey, true);
  window.removeEventListener('resize', position);
});
</script>

<template>
  <div ref="anchor" class="input font-trigger">
    <button type="button" data-field="font-family" :style="{ fontFamily: value }" :title="value" @click="toggle">
      <span class="font-name">{{ label }}</span>
      <span class="font-chev">▾</span>
    </button>
  </div>

  <Teleport to="body">
    <div v-if="open" class="font-pop" :style="style" data-field="font-pop" role="listbox" aria-label="字体">
      <button
        v-for="(f, i) in FONT_LIST"
        :key="f.value"
        type="button"
        role="option"
        class="font-row"
        :class="{ 'is-active': f.value === value }"
        :aria-selected="f.value === value"
        :data-field="`font-row-${i}`"
        :style="{ fontFamily: f.value }"
        :title="f.value"
        @click="emit('change', f.value)"
      >
        {{ f.label }}
      </button>
    </div>
  </Teleport>
</template>
